import React, { useState } from 'react';
import { Users, Building2, Truck, Package, ShoppingCart, Tag, History, LogOut, ChevronLeft, ChevronDown, ChevronRight, CreditCard, BarChart3, FileBarChart, ChartBarStacked, Stamp, ChartBarDecreasing, ChartGantt, ShoppingBasket } from 'lucide-react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { MENU_ROLES, DASHBOARD_ROUTE } from '../../config/menuRoles';
import { authService } from '../../services/authService';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCadastroOpen, setIsCadastroOpen] = useState(true);
  const [isRelatoriosOpen, setIsRelatoriosOpen] = useState(true);
  const companyName = authService.getCompanyName();
  const getInitials = (name: string) => {
    if (!name) return 'CA';
    const words = name
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .filter(Boolean);
    const initials = (words[0]?.[0] || '') + (words[1]?.[0] || '');
    return (initials || 'CA').toUpperCase();
  };
  const brandInitials = getInitials(companyName || 'Comercial Admin');

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const userProfile = authService.getUserProfile();

  // Itens principais com restrição por perfil (centralizados em config)
  const topItems = [
    { icon: BarChart3, label: 'Paineis', path: DASHBOARD_ROUTE, roles: MENU_ROLES.topItems[DASHBOARD_ROUTE] as string[] },
    { icon: History, label: 'Caixa', path: '/caixas', roles: MENU_ROLES.topItems['/caixas'] as string[] },
    { icon: ShoppingCart, label: 'Vendas', path: '/vendas', roles: MENU_ROLES.topItems['/vendas'] as string[] },
    { icon: ShoppingBasket, label: 'Compras', path: '/compras', roles: MENU_ROLES.topItems['/compras'] as string[] },
    { icon: Users, label: 'Usuários', path: '/usuarios', roles: MENU_ROLES.topItems['/usuarios'] as string[] },
    { icon: Building2, label: 'Empresas', path: '/empresas', roles: MENU_ROLES.topItems['/empresas'] as string[] },
    { icon: CreditCard, label: 'Contas de Clientes', path: '/clientes/contas', roles: MENU_ROLES.topItems['/clientes/contas'] as string[] },
  ].filter((item) => !item.roles || (userProfile ? item.roles.includes(userProfile) : false));

  // Grupo Cadastro com restrição por perfil (centralizados em config)
  const cadastroItems: Array<{ icon: any; label: string; path?: string; disabled?: boolean; roles?: string[] }> = [
    { icon: Package, label: 'Produtos', path: '/produtos', roles: MENU_ROLES.cadastroItems['/produtos'] as string[] },
    { icon: ChartBarStacked, label: 'Categorias', path: '/categorias', roles: MENU_ROLES.cadastroItems['/categorias'] as string[] },
    { icon: Stamp, label: 'Marcas', path: '/marcas', roles: MENU_ROLES.cadastroItems['/marcas'] as string[] },
    { icon: Package, label: 'Embalagens', path: '/embalagens', roles: MENU_ROLES.cadastroItems['/embalagens'] as string[] },
    { icon: Truck, label: 'Fornecedores', path: '/fornecedores', roles: MENU_ROLES.cadastroItems['/fornecedores'] as string[] },
    { icon: CreditCard, label: 'Tipo Pagamento', path: '/configuracoes/tipos-pagamento', roles: MENU_ROLES.cadastroItems['/configuracoes/tipos-pagamento'] as string[] },
    { icon: Users, label: 'Clientes', path: '/clientes', roles: MENU_ROLES.cadastroItems['/clientes'] as string[] },
  ].filter((item) => !item.roles || (userProfile ? item.roles.includes(userProfile) : false));

  const relatoriosItems: Array<{ icon: any; label: string; path?: string; disabled?: boolean; roles?: string[] }> = [
    { icon: FileBarChart, label: 'Rel. Vendas', path: '/relatorios/vendas', roles: MENU_ROLES.reportsItems['/relatorios/vendas'] as string[] },
    { icon: ChartBarDecreasing, label: 'Rel. Pagamento', path: '/relatorios/pagamentos', roles: MENU_ROLES.reportsItems['/relatorios/pagamentos'] as string[] },
    { icon: ChartGantt, label: 'Rel. Estoque', path: '/relatorios/estoque-movimentos', roles: MENU_ROLES.reportsItems['/relatorios/estoque-movimentos'] as string[] },
  ].filter((item) => !item.roles || (userProfile ? item.roles.includes(userProfile) : false));

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full flex flex-col bg-gradient-to-b from-white to-gray-50 shadow-berry-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } w-64 border-r border-gray-100`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-white">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-berry flex items-center justify-center shadow-berry">
              <span className="text-white font-bold text-lg">{brandInitials}</span>
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-lg text-gray-800">{companyName || 'Comercial Admin'}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-berry hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-red-600" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 mt-2 overflow-y-auto">
          <div className="mb-4">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3">Menu Principal</span>
          </div>
          {/* Itens de topo */}
          <ul className="space-y-1 mb-4">
            {topItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`group flex items-center space-x-3 px-4 py-3 rounded-berry transition-all duration-200 relative ${
                      isActive
                        ? 'bg-gradient-to-r from-primary-50 to-primary-100 text-red-700 shadow-sm border-l-4 border-primary-600'
                        : 'text-gray-600 hover:bg-white hover:text-gray-900 hover:shadow-sm'
                    }`}
                  >
                    <div className={`p-1 rounded-lg transition-colors ${
                      isActive ? 'bg-primary-100' : 'group-hover:bg-gray-100'
                    }`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="font-medium">{item.label}</span>
                    {isActive && (
                      <div className="absolute right-3 w-2 h-2 bg-primary-600 rounded-full"></div>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* Grupo Cadastro (oculto quando vazio) */}
          {cadastroItems.length > 0 && (
            <>
              <div
                className="mb-2 px-3 flex items-center justify-between cursor-pointer select-none"
                onClick={() => setIsCadastroOpen((v) => !v)}
              >
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Cadastro</span>
                {isCadastroOpen ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
              </div>
              {isCadastroOpen && (
                <ul className="space-y-1 pl-2">
                  {cadastroItems.map((item) => {
                    const Icon = item.icon as any;
                    const isActive = item.path ? location.pathname === item.path : false;
                    if (item.disabled) {
                      return (
                        <li key={item.label}>
                          <div className="group flex items-center space-x-3 px-4 py-3 rounded-berry text-red-400 bg-gray-50 cursor-not-allowed">
                            <div className="p-1 rounded-lg bg-gray-100">
                              <Icon className="h-5 w-5" />
                            </div>
                            <span className="font-medium">{item.label}</span>
                            <span className="ml-auto text-xs bg-gray-200 text-red-600 px-2 py-0.5 rounded-full">Em breve</span>
                          </div>
                        </li>
                      );
                    }
                    return (
                      <li key={item.path!}>
                        <Link
                          to={item.path!}
                          className={`group flex items-center space-x-3 px-4 py-3 rounded-berry transition-all duration-200 relative ${
                            isActive
                              ? 'bg-gradient-to-r from-primary-50 to-primary-100 text-red-700 shadow-sm border-l-4 border-primary-600'
                              : 'text-gray-600 hover:bg-white hover:text-gray-900 hover:shadow-sm'
                          }`}
                        >
                          <div className={`p-1 rounded-lg transition-colors ${
                            isActive ? 'bg-primary-100' : 'group-hover:bg-gray-100'
                          }`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <span className="font-medium">{item.label}</span>
                          {isActive && (
                            <div className="absolute right-3 w-2 h-2 bg-primary-600 rounded-full"></div>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}

          {/* Grupo Relatórios (oculto quando vazio) */}
          {relatoriosItems.length > 0 && (
            <>
              <div
                className="mt-4 mb-2 px-3 flex items-center justify-between cursor-pointer select-none"
                onClick={() => setIsRelatoriosOpen((v) => !v)}
              >
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Relatórios</span>
                {isRelatoriosOpen ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
              </div>
              {isRelatoriosOpen && (
                <ul className="space-y-1 pl-2">
                  {relatoriosItems.map((item) => {
                    const Icon = item.icon as any;
                    const isActive = item.path ? location.pathname === item.path : false;
                    return (
                      <li key={item.path!}>
                        <Link
                          to={item.path!}
                          className={`group flex items-center space-x-3 px-4 py-3 rounded-berry transition-all duration-200 relative ${
                            isActive
                              ? 'bg-gradient-to-r from-primary-50 to-primary-100 text-red-700 shadow-sm border-l-4 border-primary-600'
                              : 'text-gray-600 hover:bg-white hover:text-gray-900 hover:shadow-sm'
                          }`}
                        >
                          <div className={`p-1 rounded-lg transition-colors ${
                            isActive ? 'bg-primary-100' : 'group-hover:bg-gray-100'
                          }`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <span className="font-medium">{item.label}</span>
                          {isActive && (
                            <div className="absolute right-3 w-2 h-2 bg-primary-600 rounded-full"></div>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-gray-100 bg-white">
          <button
            onClick={handleLogout}
            className="group flex items-center space-x-3 px-4 py-3 rounded-berry text-gray-600 hover:bg-red-50 hover:text-red-600 transition-all duration-200 w-full"
          >
            <div className="p-1 rounded-lg transition-colors group-hover:bg-red-100">
              <LogOut className="h-5 w-5" />
            </div>
            <span className="font-medium">Sair</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
